import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Breadcrumb } from "@/components/AppShell";
import { CustomerSheet } from "@/components/sales/CustomerSheet";
import { LinesEditor, computeLineTotals, linesToPayload, newLine } from "@/components/sales/LinesEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FREQUENCIES, PERIOD_OFFSETS } from "@/lib/recurringConstants";
import { formatKr, formatDate, todayIso, formatOrg } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ChevronsUpDown, Check, Loader2, Save, UserPlus, Building2, User, AlertTriangle, Repeat, CalendarClock, Info } from "lucide-react";

const emptyForm = (s = {}) => ({
  customer_id: "", name: "", frequency: "maned", start_date: todayIso(), end_date: "", days_before: 3, due_days: "",
  add_period_text: true, period_offset: "samme", notes: s.default_note || "", your_reference: "", our_reference: "", order_reference: "", lines: [newLine(s)],
});

/**
 * Faktura → Gjentakende → Ny avtale / Rediger avtale.
 * Støtter `?fra=<fakturaId>` (lag avtale fra eksisterende faktura) og `?customer_id=`.
 */
export default function RecurringEditorPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [meta, setMeta] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [agreement, setAgreement] = useState(null);
  const [preview, setPreview] = useState(null);
  const previewTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, sm, c, p] = await Promise.all([api.get("/sales/recurring/meta"), api.get("/sales/meta"), api.get("/sales/customers"), api.get("/sales/products")]);
        if (cancelled) return;
        setMeta({ ...m.data, invoice_settings: sm.data.invoice_settings || {} });
        setCustomers(c.data.items);
        setProducts(p.data.items);
        const s = sm.data.invoice_settings || {};
        if (editing) {
          const ag = (await api.get(`/sales/recurring/${id}`)).data;
          setAgreement(ag);
          setForm({
            customer_id: ag.customer_id || "", name: ag.name || "", frequency: ag.frequency, start_date: ag.start_date, end_date: ag.end_date || "",
            days_before: ag.days_before ?? 3, due_days: ag.due_days ?? "", add_period_text: ag.add_period_text !== false, period_offset: ag.period_offset || "samme",
            notes: ag.notes || "", your_reference: ag.your_reference || "", our_reference: ag.our_reference || "", order_reference: ag.order_reference || "",
            lines: (ag.lines || []).map((l) => ({ ...l })),
          });
        } else {
          const from = params.get("fra");
          const base = emptyForm(s);
          if (from) {
            const inv = (await api.get(`/sales/invoices/${from}`)).data;
            const firstDesc = (inv.lines || [])[0]?.description || "";
            setForm({
              ...base, customer_id: inv.customer_id || "", name: firstDesc.split(" – ")[0].slice(0, 80) || inv.customer?.name || "",
              notes: inv.notes || "", your_reference: inv.your_reference || "", our_reference: inv.our_reference || "", order_reference: inv.order_reference || "",
              lines: (inv.lines || []).map((l) => ({ ...l, id: `tmp-${Math.random().toString(36).slice(2)}` })),
            });
          } else {
            setForm({ ...base, customer_id: params.get("customer_id") || "" });
          }
        }
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Forhåndsvisning av kommende fakturadatoer (debounced)
  useEffect(() => {
    if (loading) return;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      api.post("/sales/recurring/preview", {
        frequency: form.frequency, start_date: form.start_date || null, end_date: form.end_date || null, days_before: Number(form.days_before) || 0,
        period_offset: form.period_offset, add_period_text: form.add_period_text, sample_description: form.lines[0]?.description || "",
      }).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, 250);
    return () => clearTimeout(previewTimer.current);
  }, [loading, form.frequency, form.start_date, form.end_date, form.days_before, form.period_offset, form.add_period_text, form.lines]);

  const customer = customers.find((c) => c.id === form.customer_id);
  const totals = useMemo(() => computeLineTotals(form.lines), [form.lines]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const s = meta?.invoice_settings || {};
  const firstPeriod = preview?.items?.[0]?.period_label || "";

  const validate = () => {
    if (!form.customer_id) return "Velg en kunde";
    if (!form.start_date) return "Velg første fakturadato";
    if (form.end_date && form.end_date < form.start_date) return "Sluttdato kan ikke være før startdato";
    if (!form.lines.length) return "Legg til minst én linje";
    if (form.lines.some((l) => !String(l.description || "").trim())) return "Alle linjer må ha en beskrivelse";
    if (form.lines.some((l) => l.unit_price === "" || Number.isNaN(Number(l.unit_price)))) return "Alle linjer må ha en pris";
    if (totals.total <= 0) return "Fakturabeløpet må være større enn 0";
    return "";
  };

  const onSave = async () => {
    const v = validate();
    if (v) { toast.error(v); return; }
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id, name: form.name || null, frequency: form.frequency, start_date: form.start_date, end_date: form.end_date || null,
        days_before: Number(form.days_before) || 0, due_days: form.due_days === "" ? null : Number(form.due_days), add_period_text: !!form.add_period_text,
        period_offset: form.period_offset, notes: form.notes || null, your_reference: form.your_reference || null, our_reference: form.our_reference || null,
        order_reference: form.order_reference || null, lines: linesToPayload(form.lines), source_invoice_id: params.get("fra") || null,
      };
      const r = editing ? await api.put(`/sales/recurring/${id}`, payload) : await api.post("/sales/recurring", payload);
      const n = r.data.created_drafts || 0;
      toast.success(editing ? "Avtalen er oppdatert" : "Avtalen er opprettet", { description: n ? `${n} fakturakladd${n === 1 ? "" : "er"} er klar${n === 1 ? "" : "e"} for godkjenning.` : `Første kladd lages ${formatDate(r.data.next_generate_on)}.` });
      window.dispatchEvent(new Event("rp:refresh-recurring"));
      navigate("/faktura/gjentakende");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert variant="destructive"><AlertTitle>Kunne ikke laste editoren</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>;
  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-56" /><Skeleton className="h-64" /><Skeleton className="h-64" /></div>;

  const offsetHint = PERIOD_OFFSETS.find((o) => o.value === form.period_offset)?.hint;

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Faktura", to: "/faktura" }, { label: "Gjentakende", to: "/faktura/gjentakende" }, { label: editing ? "Rediger avtale" : "Ny avtale" }]} />
      <PageHeader title={editing ? `Rediger avtale${agreement?.name ? ` – ${agreement.name}` : ""}` : "Ny gjentakende avtale"} description="Controlle lager en fakturakladd automatisk før hver fakturadato. Du kontrollerer og utsteder – ingenting sendes uten din godkjenning." />

      {agreement?.status === "pauset" && agreement?.pause_reason ? (
        <Alert className="border-[#FFE2A3] dark:border-[#8A5A00]/50" data-testid="recurring-editor-paused-alert"><AlertTriangle className="h-4 w-4" /><AlertTitle>Avtalen er pauset</AlertTitle><AlertDescription>{agreement.pause_reason}</AlertDescription></Alert>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_340px] items-start">
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Kunde og navn</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <Popover open={custOpen} onOpenChange={setCustOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={custOpen} className="flex-1 justify-between h-11 font-normal" data-testid="recurring-customer-picker">
                      {customer ? <span className="flex items-center gap-2 truncate">{customer.type === "person" ? <User className="h-4 w-4 text-muted-foreground" /> : <Building2 className="h-4 w-4 text-muted-foreground" />}<span className="truncate">{customer.name}</span><span className="text-xs text-muted-foreground">#{customer.customer_number}</span></span> : <span className="text-muted-foreground">Velg kunde…</span>}
                      <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-card" align="start">
                    <Command>
                      <CommandInput placeholder="Søk kunde…" data-testid="recurring-customer-search" />
                      <CommandList>
                        <CommandEmpty>Ingen kunder funnet.</CommandEmpty>
                        <CommandGroup>
                          {customers.map((c) => (
                            <CommandItem key={c.id} value={`${c.name} ${c.customer_number} ${c.email || ""}`} onSelect={() => { setForm((f) => ({ ...f, customer_id: c.id })); setCustOpen(false); }} data-testid={`recurring-customer-option-${c.id}`}>
                              <Check className={cn("h-4 w-4 mr-2", form.customer_id === c.id ? "opacity-100" : "opacity-0")} />
                              <span className="flex-1 truncate">{c.name}</span>
                              <span className="text-xs text-muted-foreground">#{c.customer_number}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                        <CommandSeparator />
                        <CommandGroup>
                          <CommandItem onSelect={() => { setCustOpen(false); setSheetOpen(true); }} data-testid="recurring-customer-create"><UserPlus className="h-4 w-4 mr-2" /> Ny kunde…</CommandItem>
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <Button variant="outline" className="h-11" onClick={() => setSheetOpen(true)} data-testid="recurring-new-customer-button"><UserPlus className="h-4 w-4 mr-2" /> Ny kunde</Button>
              </div>
              {customer ? (
                <div className="rounded-lg border bg-secondary/50 p-3 text-sm grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="recurring-customer-summary">
                  <p className="font-medium">{customer.name}</p>
                  <p className="text-muted-foreground">{customer.org_number ? `Org.nr ${formatOrg(customer.org_number)}` : customer.type === "person" ? "Privatperson" : ""}</p>
                  <p className="text-muted-foreground">{[customer.address, [customer.postal_code, customer.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || <span className="text-warn">Adresse mangler</span>}</p>
                  <p className="text-muted-foreground">{customer.email || <span className="text-warn">E-post mangler (trengs for sending)</span>}</p>
                </div>
              ) : null}
              <div className="space-y-1.5">
                <Label htmlFor="r-name">Navn på avtalen <span className="text-muted-foreground">(valgfritt)</span></Label>
                <Input id="r-name" value={form.name} onChange={set("name")} placeholder={customer ? `F.eks. Husleie ${customer.name}` : "F.eks. Husleie kontor, Vedlikeholdsavtale"} maxLength={120} data-testid="recurring-name-input" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Plan</CardTitle><CardDescription>Hvor ofte, fra når, og hvor tidlig kladden skal ligge klar.</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Frekvens</Label>
                <Select value={form.frequency} onValueChange={set("frequency")}>
                  <SelectTrigger data-testid="recurring-frequency-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value} data-testid={`recurring-frequency-${f.value}`}>{f.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label htmlFor="r-start">{editing && agreement?.generated_count ? "Startdato" : "Første fakturadato"}</Label><Input id="r-start" type="date" value={form.start_date} onChange={set("start_date")} data-testid="recurring-start-date" /></div>
              <div className="space-y-1.5"><Label htmlFor="r-end">Sluttdato <span className="text-muted-foreground">(valgfritt)</span></Label><Input id="r-end" type="date" value={form.end_date} min={form.start_date} onChange={set("end_date")} data-testid="recurring-end-date" /></div>
              <div className="space-y-1.5"><Label htmlFor="r-days-before">Lag kladd (dager før)</Label><Input id="r-days-before" type="number" min="0" max="60" value={form.days_before} onChange={set("days_before")} className="tnum" data-testid="recurring-days-before" /></div>
              <div className="space-y-1.5"><Label htmlFor="r-due">Forfall (dager)</Label><Input id="r-due" type="number" min="0" max="365" value={form.due_days} onChange={set("due_days")} placeholder={String(customer?.default_due_days ?? meta?.default_due_days ?? 14)} className="tnum" data-testid="recurring-due-days" /><p className="text-[11px] text-muted-foreground">Tomt = kundens/standard betalingsfrist</p></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div><CardTitle className="text-base">Periodetekst</CardTitle><CardDescription>Legg automatisk til perioden i linjetekstene, f.eks. «Husleie – {firstPeriod || "januar 2027"}».</CardDescription></div>
                <Switch checked={form.add_period_text} onCheckedChange={set("add_period_text")} aria-label="Legg til periodetekst" data-testid="recurring-period-text-switch" />
              </div>
            </CardHeader>
            {form.add_period_text ? (
              <CardContent className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Perioden gjelder</Label>
                  <Select value={form.period_offset} onValueChange={set("period_offset")}>
                    <SelectTrigger data-testid="recurring-period-offset-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card">{PERIOD_OFFSETS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`recurring-period-offset-${o.value}`}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {offsetHint ? <p className="text-[11px] text-muted-foreground">{offsetHint}</p> : null}
                </div>
                {preview?.items?.[0]?.sample_line ? (
                  <div className="rounded-lg border bg-secondary/50 p-3 text-sm self-end" data-testid="recurring-sample-line">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Første linje blir</p>
                    <p className="font-medium truncate">{preview.items[0].sample_line}</p>
                  </div>
                ) : null}
              </CardContent>
            ) : null}
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Linjer</CardTitle><CardDescription>Det faste innholdet på hver faktura.</CardDescription></CardHeader>
            <CardContent>
              <LinesEditor lines={form.lines} onChange={(lines) => setForm((f) => ({ ...f, lines }))} products={products} settings={s} testPrefix="recurring" periodSuffix={form.add_period_text ? firstPeriod : ""} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Referanser og melding</CardTitle></CardHeader>
            <CardContent className="grid gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5"><Label htmlFor="r-yref">Deres referanse</Label><Input id="r-yref" value={form.your_reference} onChange={set("your_reference")} placeholder="Kontaktperson hos kunde" data-testid="recurring-your-ref" /></div>
                <div className="space-y-1.5"><Label htmlFor="r-oref">Vår referanse</Label><Input id="r-oref" value={form.our_reference} onChange={set("our_reference")} placeholder="Ditt navn" data-testid="recurring-our-ref" /></div>
                <div className="space-y-1.5"><Label htmlFor="r-order">Ordre/prosjekt</Label><Input id="r-order" value={form.order_reference} onChange={set("order_reference")} placeholder="Avtalenr eller prosjekt" data-testid="recurring-order-ref" /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="r-notes">Melding på fakturaen</Label><Textarea id="r-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="F.eks. Takk for samarbeidet! Vises nederst på fakturaen." data-testid="recurring-notes" /></div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:sticky lg:top-24 space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Repeat className="h-4 w-4 text-primary" /> Per faktura</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm" data-testid="recurring-summary">
              <div className="flex justify-between"><span className="text-muted-foreground">Sum eks. mva</span><span className="tnum">{formatKr(totals.subtotal)}</span></div>
              {totals.breakdown.map((b) => <div key={b.rate} className="flex justify-between"><span className="text-muted-foreground">MVA {b.rate} %</span><span className="tnum">{formatKr(b.vat)}</span></div>)}
              <div className="flex justify-between border-t pt-3 mt-3"><span className="font-medium">Å betale</span><span className="font-heading text-xl font-semibold tnum" data-testid="recurring-total">{formatKr(totals.total)}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" /> Kommende</CardTitle><CardDescription>Slik blir de neste fakturaene.</CardDescription></CardHeader>
            <CardContent className="p-0">
              {!preview ? <div className="p-4"><Skeleton className="h-16" /></div> : preview.items.length === 0 ? (
                <p className="px-4 pb-4 text-sm text-muted-foreground" data-testid="recurring-preview-empty">Ingen fakturadatoer før sluttdatoen. Flytt sluttdatoen fram.</p>
              ) : (
                <ul className="divide-y" data-testid="recurring-preview-list">
                  {preview.items.map((it, i) => (
                    <li key={it.invoice_date} className="px-4 py-2.5 text-sm" data-testid={`recurring-preview-item-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium tnum">{formatDate(it.invoice_date)}</span>
                        {it.immediate ? <Badge variant="outline" className="status-ma_kontrolleres border text-[11px]">Kladd lages nå</Badge> : <span className="text-xs text-muted-foreground tnum">kladd {formatDate(it.generate_on)}</span>}
                      </div>
                      {form.add_period_text ? <p className="text-xs text-muted-foreground truncate">Periode: {it.period_label}</p> : null}
                    </li>
                  ))}
                  {preview.ends ? <li className="px-4 py-2 text-xs text-muted-foreground">Avtalen avsluttes etter siste dato.</li> : null}
                </ul>
              )}
            </CardContent>
          </Card>

          {preview?.immediate_count > 1 && !editing ? (
            <Alert data-testid="recurring-catchup-alert"><Info className="h-4 w-4" /><AlertTitle>{preview.immediate_count} kladder lages straks</AlertTitle><AlertDescription>Startdatoen er tilbake i tid, så det lages én kladd per periode fram til i dag. Slett dem du ikke trenger.</AlertDescription></Alert>
          ) : null}

          <div className="grid gap-2">
            <Button size="lg" onClick={onSave} disabled={busy} data-testid="recurring-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} {editing ? "Lagre endringer" : "Opprett avtale"}</Button>
            <Button variant="ghost" onClick={() => navigate("/faktura/gjentakende")} disabled={busy} data-testid="recurring-cancel-button">Avbryt</Button>
            {!meta?.profile_completeness?.complete ? <p className="text-xs text-warn">Kladder kan lages, men du må fullføre <Link to="/profil" className="underline">firmaprofilen</Link> før de kan utstedes.</p> : null}
          </div>
        </div>
      </div>

      <CustomerSheet open={sheetOpen} onOpenChange={setSheetOpen} onSaved={(c) => { setCustomers((cs) => [...cs.filter((x) => x.id !== c.id), c].sort((a, b) => a.name.localeCompare(b.name))); setForm((f) => ({ ...f, customer_id: c.id })); }} />
    </div>
  );
}
